import React, { useMemo, useState } from 'react';
import './AddOnRowsEditor.css';
import ActionButton from '../input/ActionButton';
import CustomDropdown from '../input/CustomDropdown';
import Textfield from '../input/Textfield';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import { parsePriceInput, samePrice, toPriceInput } from '../../util/eventPrices';
import {
  ADD_ON_MAX_QUANTITY_MAX, ADD_ON_PRICE_MAX, formatAddOnPrice, groupPrice, settingsAt,
} from '../../util/addOns';

// ── Row model ──────────────────────────────────────────────────────────────────────────────────
// One sign-up add-on row of a process template (mode "template") or a process (mode "process"):
// { key, id, addOnId, addOnName, price, maxQuantity, bookableOnHomepage, groupPrices }
// - key: client key (React key, stable while editing) — the server id is kept separately (null = new).
// - price / maxQuantity: input strings. Template: '' = catalog value at the process location (resolved when a
//   process is created). Process: required.
// - bookableOnHomepage: template null = like the catalog, true/false = own value; process true/false.
// - groupPrices: { [participantGroupId]: input string } — '' = none. Template: none = catalog group price, else
//   the row price; process: none = the row price.
// - addOnName: what the server sent with the row (label while the catalog loads or when the entry is gone).
// - initialGroupIds: groups that carried a price when the row was loaded or prefilled — their inputs stay in the
//   "Gruppenpreise" panel while emptied (archived/guest groups are otherwise only shown while they hold a value).
// The add-on of a stored process row is fixed (the API refuses changing it — remove + add instead).
export const ADD_ON_ROW_MODE = Object.freeze({ template: 'template', process: 'process' });

let keySeq = 0;
export const nextAddOnRowKey = () => `addOnRow${++keySeq}`;

export const newAddOnRow = (mode = ADD_ON_ROW_MODE.template) => ({
  key: nextAddOnRowKey(),
  id: null,
  addOnId: null,
  addOnName: '',
  price: '',
  maxQuantity: '',
  bookableOnHomepage: mode === ADD_ON_ROW_MODE.process ? true : null,
  groupPrices: {},
  initialGroupIds: [],
});

const groupPricesToInputs = (list) => {
  const map = {};
  (Array.isArray(list) ? list : []).forEach(p => {
    if (p?.participantGroupId == null || p.price == null) return;
    map[Number(p.participantGroupId)] = toPriceInput(p.price);
  });
  return map;
};

// Group ids that hold a value in a groupPrices input map.
const groupIdsWithValue = (map) => Object.keys(map ?? {})
  .filter(id => String(map[id] ?? '').trim() !== '')
  .map(Number);

const groupPricesToRequest = (map) => Object.keys(map ?? {})
  .map(id => ({ participantGroupId: Number(id), price: parsePriceInput(map[id]) }))
  .filter(p => p.price != null && !Number.isNaN(p.price));

const countInput = (value) => (value == null || value === '' ? '' : String(value));

// Whole number input → number, null when empty, NaN when invalid / out of range.
function parseCountInput(value, max = ADD_ON_MAX_QUANTITY_MAX) {
  const raw = String(value ?? '').trim();
  if (raw === '') return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0 || n > max) return NaN;
  return n;
}

// ProcessTemplateAddOnDto → row (template mode).
export const templateAddOnToRow = (dto) => {
  const groupPrices = groupPricesToInputs(dto?.participantGroupPrices);
  return {
    key: nextAddOnRowKey(),
    id: dto?.id ?? null,
    addOnId: dto?.addOnId != null ? Number(dto.addOnId) : null,
    addOnName: dto?.addOnName ?? '',
    price: toPriceInput(dto?.price),
    maxQuantity: countInput(dto?.maxQuantity),
    bookableOnHomepage: typeof dto?.bookableOnHomepage === 'boolean' ? dto.bookableOnHomepage : null,
    groupPrices,
    initialGroupIds: groupIdsWithValue(groupPrices),
  };
};

// EventProcessAddOnDto → row (process mode).
export const processAddOnToRow = (dto) => {
  const groupPrices = groupPricesToInputs(dto?.participantGroupPrices);
  return {
    key: nextAddOnRowKey(),
    id: dto?.id ?? null,
    addOnId: dto?.addOnId != null ? Number(dto.addOnId) : null,
    addOnName: dto?.addOnName ?? '',
    price: toPriceInput(dto?.price),
    maxQuantity: countInput(dto?.maxQuantity ?? 0),
    bookableOnHomepage: dto?.bookableOnHomepage === true,
    groupPrices,
    initialGroupIds: groupIdsWithValue(groupPrices),
  };
};

// Rows → ProcessTemplateAddOnDto[] (array order = display order; empty values = catalog).
export const addOnRowsToTemplateRequest = (rows) => (rows ?? []).map(r => ({
  id: r.id ?? null,
  addOnId: r.addOnId,
  price: parsePriceInput(r.price),
  maxQuantity: parseCountInput(r.maxQuantity),
  bookableOnHomepage: typeof r.bookableOnHomepage === 'boolean' ? r.bookableOnHomepage : null,
  participantGroupPrices: groupPricesToRequest(r.groupPrices),
}));

// Rows → ProcessAddOnInput[] (SaveProcessAddOnsRequest.addOns, full replace by id).
export const addOnRowsToProcessRequest = (rows) => (rows ?? []).map(r => ({
  id: r.id ?? null,
  addOnId: r.addOnId,
  price: parsePriceInput(r.price),
  maxQuantity: parseCountInput(r.maxQuantity),
  bookableOnHomepage: r.bookableOnHomepage === true,
  participantGroupPrices: groupPricesToRequest(r.groupPrices),
}));

// Catalog values a new process row starts with (rule 2 without template row) — input strings.
function processDefaults(addOn, locationId) {
  const s = settingsAt(addOn, locationId);
  if (!s) return null;
  return {
    price: toPriceInput(s.price),
    maxQuantity: String(s.maxQuantity),
    bookableOnHomepage: s.bookableOnHomepage,
    groupPrices: groupPricesToInputs(addOn.participantGroupPrices),
  };
}

const samePriceInput = (a, b) => {
  const pa = parsePriceInput(a);
  const pb = parsePriceInput(b);
  if (pa == null || pb == null || Number.isNaN(pa) || Number.isNaN(pb)) return String(a ?? '').trim() === String(b ?? '').trim();
  return samePrice(pa, pb);
};

const sameGroupPrices = (a, b) => {
  const left = groupPricesToRequest(a);
  const right = groupPricesToRequest(b);
  return left.length === right.length
    && left.every(p => right.some(q => q.participantGroupId === p.participantGroupId && samePrice(q.price, p.price)));
};

// Problems per row (Map key → texts), in the wording of the server validator. Duplicates are flagged on the
// later row. groupsById: Map from useParticipantGroups() (group names in the messages).
export function addOnRowErrors(rows, { mode = ADD_ON_ROW_MODE.template, groupsById } = {}) {
  const errors = new Map();
  const seen = new Set();
  const processMode = mode === ADD_ON_ROW_MODE.process;
  (rows ?? []).forEach(row => {
    const list = [];
    if (row.addOnId == null) {
      list.push('Bitte einen Zusatz wählen.');
    } else {
      if (seen.has(Number(row.addOnId))) list.push('Dieser Zusatz ist schon eingetragen.');
      seen.add(Number(row.addOnId));
    }
    const price = parsePriceInput(row.price);
    if (price == null) {
      if (processMode) list.push('Bitte einen Preis angeben (0 = kostenlos).');
    } else if (Number.isNaN(price) || price > ADD_ON_PRICE_MAX) {
      list.push('Der Preis muss zwischen 0 und 10.000 € liegen.');
    }
    const max = parseCountInput(row.maxQuantity);
    if (max == null) {
      if (processMode) list.push('Bitte eine Höchstmenge angeben (0 = unbegrenzt).');
    } else if (Number.isNaN(max)) {
      list.push('Die Höchstmenge muss eine ganze Zahl von 0 bis 10.000 sein (0 = unbegrenzt).');
    }
    Object.keys(row.groupPrices ?? {}).forEach(id => {
      const value = parsePriceInput(row.groupPrices[id]);
      if (value != null && (Number.isNaN(value) || value > ADD_ON_PRICE_MAX)) {
        const name = groupsById?.get(Number(id))?.name;
        list.push(name
          ? `Gruppenpreis für „${name}“: bitte 0 bis 10.000 € eingeben oder das Feld leeren.`
          : 'Gruppenpreise: bitte 0 bis 10.000 € eingeben oder das Feld leeren.');
      }
    });
    if (list.length > 0) errors.set(row.key, list);
  });
  return errors;
}

// One message for the whole list ('' = fine) — the rows show the details.
export const addOnRowsIssue = (rows, options) => (addOnRowErrors(rows, options).size > 0
  ? 'Zusätze: Bitte die markierten Einträge prüfen.'
  : '');

// "max. 8 je Spieltag" / "unbegrenzt"
export const formatMaxPerEvent = (max, eventNoun = 'Spieltag') =>
  (Number(max) > 0 ? `max. ${Number(max)} je ${eventNoun}` : 'unbegrenzt');

// Location deviations of a catalog entry: "Halle Pforzheim 20,00 €, Eigenes Gelände entfällt" ('' without).
const deviationsText = (addOn) => (addOn?.locationSettings ?? [])
  .map(s => `${s.locationName || `Location #${s.locationId}`} ${s.price == null ? 'entfällt' : formatAddOnPrice(s.price)}`)
  .join(', ');

/**
 * Row editor of the sign-up add-ons ("Zusätze") of a process template (mode "template") or of one process
 * (mode "process"). One row = add-on + Preis + Max. je Spieltag + Homepage + remove, below it an expandable
 * "Gruppenpreise" panel and a catalog line; "Zusatz hinzufügen" appends.
 * - template: every value is optional — empty = catalog at the process location (placeholders show the catalog
 *   standard, the line below its location deviations); Homepage "Wie Katalog" / "Ja" / "Nein".
 * - process: concrete values; picking an add-on prefills them from the catalog at `locationId` (as long as the
 *   fields still hold the previous entry's catalog values). Only add-ons offered at that location can be picked;
 *   the add-on of a stored row is fixed.
 * Controlled: `rows` in, `onChange(nextRows)` out. Errors per row only after a save attempt (showErrors).
 * addOns: catalog (hooks/useAddOns, may still be loading); groups: all participant groups (useParticipantGroups
 * — active groups are offered, archived ones only while they carry a value or carried one when the row was loaded;
 * the guest group likewise, because the row price is the guest price).
 */
function AddOnRowsEditor({
  mode = ADD_ON_ROW_MODE.template, rows, onChange, addOns, addOnsLoaded = false, addOnsFailed = false,
  groups, showErrors = false, disabled = false, locationId = null, locationName = '', eventNoun = 'Spieltag',
  emptyText,
}) {
  const processMode = mode === ADD_ON_ROW_MODE.process;
  const list = rows ?? [];
  const catalog = useMemo(() => addOns ?? [], [addOns]);
  const catalogById = useMemo(() => new Map(catalog.map(a => [Number(a.id), a])), [catalog]);
  const groupsById = useMemo(() => new Map((groups ?? []).map(g => [g.id, g])), [groups]);
  // Process mode: only add-ons offered at the process location (the API refuses others).
  const offered = useMemo(
    () => (processMode ? catalog.filter(a => settingsAt(a, locationId) != null) : catalog),
    [catalog, processMode, locationId],
  );
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const errors = showErrors ? addOnRowErrors(list, { mode, groupsById }) : null;
  const loadingText = addOnsFailed ? 'Konnte nicht geladen werden' : 'Lädt …';
  const atLocation = locationName ? `an ${locationName}` : 'an der Prozess-Location';
  const forLocation = locationName ? `für ${locationName}` : 'für die Prozess-Location';
  const usedIds = new Set(list.map(r => (r.addOnId != null ? Number(r.addOnId) : null)).filter(id => id != null));

  const patchRow = (key, changes) => onChange(list.map(r => (r.key === key ? { ...r, ...changes } : r)));
  const removeRow = (key) => onChange(list.filter(r => r.key !== key));
  const addRow = () => onChange([...list, newAddOnRow(mode)]);
  const toggleGroups = (key) => setOpenKeys(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const changeAddOn = (row, value) => {
    const addOnId = value ? Number(value) : null;
    const next = addOnId != null ? catalogById.get(addOnId) : null;
    const changes = { addOnId, addOnName: next?.name ?? '' };
    if (processMode && next) {
      // Prefill from the catalog at the process location while the fields hold no own input yet.
      const previous = row.addOnId != null ? catalogById.get(Number(row.addOnId)) : null;
      const before = previous ? processDefaults(previous, locationId) : null;
      const after = processDefaults(next, locationId);
      if (after) {
        const untouched = (field, same) => (row[field] === '' || (before && same(row[field], before[field])));
        if (untouched('price', samePriceInput)) changes.price = after.price;
        if (untouched('maxQuantity', (a, b) => String(a).trim() === String(b))) changes.maxQuantity = after.maxQuantity;
        if (!before || row.bookableOnHomepage === before.bookableOnHomepage) changes.bookableOnHomepage = after.bookableOnHomepage;
        const noOwnGroupPrices = groupPricesToRequest(row.groupPrices).length === 0;
        if (noOwnGroupPrices || (before && sameGroupPrices(row.groupPrices, before.groupPrices))) {
          changes.groupPrices = after.groupPrices;
          // Prefilled groups stay editable while emptied, like loaded ones.
          changes.initialGroupIds = [...new Set([...(row.initialGroupIds ?? []), ...groupIdsWithValue(after.groupPrices)])];
        }
      }
    }
    patchRow(row.key, changes);
  };

  const patchGroupPrice = (row, groupId, value) => patchRow(row.key, {
    groupPrices: { ...(row.groupPrices ?? {}), [groupId]: value },
  });

  // Groups of the panel: active non-guest groups + every group that carries a value (archived, guest) or carried
  // one when the row was loaded/prefilled — so emptying the field to retype does not unmount the input.
  const panelGroups = (row) => {
    const initial = new Set((row.initialGroupIds ?? []).map(Number));
    return (groups ?? []).filter(g => {
      const has = String(row.groupPrices?.[g.id] ?? '').trim() !== '';
      return has || initial.has(g.id) || (!g.isArchived && !g.isGuest);
    });
  };

  // Template: catalog standard + deviations + catalog group prices; empty fields fall back to them.
  const templateLine = (addOn) => {
    if (!addOn) return null;
    const standard = settingsAt(addOn, null);
    const deviations = deviationsText(addOn);
    return (
      <>
        Leer = Katalog: {standard
          ? <>Standard <strong>{formatAddOnPrice(standard.price)}</strong> · {formatMaxPerEvent(standard.maxQuantity, eventNoun)} · Homepage {standard.bookableOnHomepage ? 'ja' : 'nein'}</>
          : <>Standard <strong>entfällt</strong></>}
        {deviations && <> · abweichend: {deviations}</>}
        {!standard && <> – Prozesse an anderen Locations übernehmen den Zusatz nicht</>}
      </>
    );
  };

  // Process: catalog values at the location when the row differs from them (or the entry is not offered there).
  const processLine = (row, addOn) => {
    if (!addOn) return null;
    const s = settingsAt(addOn, locationId);
    if (!s) return { warning: true, text: <>Laut Katalog entfällt „{addOn.name}“ {atLocation}.</> };
    const differs = !samePriceInput(row.price, toPriceInput(s.price))
      || String(row.maxQuantity).trim() !== String(s.maxQuantity)
      || row.bookableOnHomepage !== s.bookableOnHomepage;
    if (!differs) return null;
    return {
      warning: false,
      text: <>Im Katalog {forLocation}: {formatAddOnPrice(s.price)} · {formatMaxPerEvent(s.maxQuantity, eventNoun)} · Homepage {s.bookableOnHomepage ? 'ja' : 'nein'}</>,
    };
  };

  const renderGroupPanel = (row, addOn, rowName, open) => {
    const shown = panelGroups(row);
    const rowPrice = parsePriceInput(row.price);
    const standard = !processMode && addOn ? settingsAt(addOn, null) : null;
    // Placeholder = what applies while the field is empty.
    const placeholderFor = (g) => {
      if (processMode) return rowPrice != null && !Number.isNaN(rowPrice) ? toPriceInput(rowPrice) : '';
      const catalogGroup = addOn ? groupPrice(addOn, g.id) : null;
      if (catalogGroup != null) return toPriceInput(catalogGroup);
      if (rowPrice != null && !Number.isNaN(rowPrice)) return toPriceInput(rowPrice);
      return standard ? toPriceInput(standard.price) : '';
    };
    const ownCount = groupPricesToRequest(row.groupPrices).length;
    const panelId = `addOnRowGroups-${row.key}`;
    return (
      <div className={`addOnRowGroups${open ? ' isOpen' : ''}`}>
        <button
          type="button"
          className="addOnRowGroupsToggle"
          onClick={() => toggleGroups(row.key)}
          aria-expanded={open}
          aria-controls={panelId}
          disabled={disabled}
        >
          <span className="material-symbols-rounded" aria-hidden="true">groups</span>
          <span>Gruppenpreise</span>
          {ownCount > 0 && <span className="addOnRowGroupsCount">{ownCount}</span>}
          <span className="material-symbols-rounded addOnRowGroupsChevron" aria-hidden="true">expand_more</span>
        </button>
        {open && (
          <div className="addOnRowGroupsPanel" id={panelId}>
            <p className="addOnRowGroupsHint">
              {processMode
                ? 'Leer = Preis der Zeile. Ein Gruppenpreis gilt für Teilnehmer dieser Gruppe (z. B. Mitglieder kostenlos).'
                : 'Leer = Gruppenpreis des Katalogs, sonst der Preis der Zeile (grau angezeigt).'}
            </p>
            {shown.length === 0 ? (
              <p className="addOnRowGroupsHint">Keine Gruppen.</p>
            ) : (
              <ul className="addOnRowGroupsList">
                {shown.map(g => (
                  <li key={g.id} className="addOnRowGroup">
                    <span className="addOnRowGroupName">
                      <ParticipantGroupDot participantGroup={g} withTooltip={false} />
                      <span>{g.name}{g.isArchived ? ' (archiviert)' : ''}</span>
                    </span>
                    <input
                      type="number" min={0} max={ADD_ON_PRICE_MAX} step={0.01} inputMode="decimal"
                      value={row.groupPrices?.[g.id] ?? ''}
                      placeholder={placeholderFor(g)}
                      disabled={disabled}
                      aria-label={`${rowName}: Preis für ${g.name} (€)`}
                      onChange={e => patchGroupPrice(row, g.id, e.target.value)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRow = (row) => {
    const addOnId = row.addOnId != null ? Number(row.addOnId) : null;
    const addOn = addOnId != null ? catalogById.get(addOnId) : null;
    const rowName = addOn?.name || row.addOnName || 'Neuer Zusatz';
    const rowErrors = errors?.get(row.key) ?? [];
    const groupError = rowErrors.some(t => t.startsWith('Gruppenpreis'));
    const open = openKeys.has(row.key) || groupError;
    const locked = processMode && row.id != null;
    const standard = !processMode && addOn ? settingsAt(addOn, null) : null;
    const line = processMode ? processLine(row, addOn) : (addOnId != null ? { warning: false, text: templateLine(addOn) } : null);

    const homepageValue = processMode
      ? (row.bookableOnHomepage ? 'yes' : 'no')
      : (typeof row.bookableOnHomepage === 'boolean' ? (row.bookableOnHomepage ? 'yes' : 'no') : 'catalog');
    const changeHomepage = (value) => patchRow(row.key, {
      bookableOnHomepage: value === 'catalog' ? null : value === 'yes',
    });

    return (
      <li key={row.key} className={`addOnRow${rowErrors.length > 0 ? ' hasErrors' : ''}`}>
        <div className="addOnRowFields">
          <div className="textFieldWrapper addOnRowSelect">
            <label className="textFieldLabel">Zusatz</label>
            {locked ? (
              <span
                className="addOnRowFixed"
                title="Der Zusatz einer gespeicherten Zeile ist fest – zum Tauschen entfernen und neu hinzufügen."
              >
                <span className="material-symbols-rounded" aria-hidden="true">inventory_2</span>
                <span className="addOnRowFixedName">{rowName}</span>
                <span className="material-symbols-rounded addOnRowFixedLock" aria-hidden="true">lock</span>
              </span>
            ) : (
              <CustomDropdown
                icon="inventory_2"
                value={addOnId != null ? String(addOnId) : ''}
                ariaLabel={`Zusatz (${rowName})`}
                onChange={e => changeAddOn(row, e.target.value)}
              >
                <option value="" disabled>{addOnsLoaded ? 'Zusatz wählen …' : loadingText}</option>
                {addOnId != null && !offered.some(a => Number(a.id) === addOnId) && (
                  <option value={String(addOnId)}>
                    {row.addOnName || addOn?.name || (addOnsLoaded ? `Zusatz #${addOnId}` : loadingText)}
                  </option>
                )}
                {offered.map(a => {
                  const id = Number(a.id);
                  const taken = id !== addOnId && usedIds.has(id);
                  const std = processMode ? null : settingsAt(a, null);
                  const label = processMode ? a.name : `${a.name} · ${std ? formatAddOnPrice(std.price) : 'entfällt im Standard'}`;
                  return (
                    <option key={id} value={String(id)} disabled={taken}>
                      {taken ? `${label} (schon eingetragen)` : label}
                    </option>
                  );
                })}
              </CustomDropdown>
            )}
          </div>

          <div className="addOnRowPrice">
            <Textfield
              label="Preis (€)"
              type="number"
              min={0}
              max={ADD_ON_PRICE_MAX}
              step={0.01}
              inputMode="decimal"
              value={row.price ?? ''}
              placeholder={processMode ? '0.00' : (standard ? toPriceInput(standard.price) : (addOn ? 'entfällt' : 'Katalog'))}
              disabled={disabled}
              aria-label={`Preis für ${rowName} (€)`}
              onChange={e => patchRow(row.key, { price: e.target.value })}
            />
          </div>

          <div className="addOnRowMax">
            <Textfield
              label={`Max. je ${eventNoun}`}
              type="number"
              min={0}
              max={ADD_ON_MAX_QUANTITY_MAX}
              step={1}
              inputMode="numeric"
              value={row.maxQuantity ?? ''}
              placeholder={processMode
                ? '0 = unbegrenzt'
                : (standard ? (standard.maxQuantity > 0 ? String(standard.maxQuantity) : 'unbegrenzt') : 'Katalog')}
              title="0 = unbegrenzt"
              disabled={disabled}
              aria-label={`Höchstmenge je ${eventNoun} für ${rowName} (0 = unbegrenzt)`}
              onChange={e => patchRow(row.key, { maxQuantity: e.target.value })}
            />
          </div>

          <div className="textFieldWrapper addOnRowHomepage">
            <label className="textFieldLabel">Homepage</label>
            <CustomDropdown
              icon="public"
              value={homepageValue}
              ariaLabel={`${rowName}: über die Homepage buchbar`}
              onChange={e => changeHomepage(e.target.value)}
            >
              {!processMode && <option value="catalog">Wie Katalog</option>}
              <option value="yes">Ja</option>
              <option value="no">Nein</option>
            </CustomDropdown>
          </div>

          <button
            type="button"
            className="addOnRowBtn isDanger"
            onClick={() => removeRow(row.key)}
            disabled={disabled}
            aria-label={`${rowName} entfernen`}
            title="Zusatz entfernen"
          >
            <span className="material-symbols-rounded" aria-hidden="true">delete</span>
          </button>
        </div>

        {line?.text && (
          <p className={`addOnRowLine${line.warning ? ' isWarning' : ''}`}>
            <span className="material-symbols-rounded" aria-hidden="true">{line.warning ? 'warning' : 'menu_book'}</span>
            <span>{line.text}</span>
          </p>
        )}

        {renderGroupPanel(row, addOn, rowName, open)}

        {rowErrors.length > 0 && (
          <ul className="addOnRowErrors">
            {rowErrors.map((text, i) => (
              <li key={i}>
                <span className="material-symbols-rounded" aria-hidden="true">error</span>
                {text}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  let catalogHint = null;
  if (addOnsLoaded && catalog.length === 0) {
    catalogHint = 'Noch keine Zusätze – lege sie auf der Preisliste an.';
  } else if (addOnsLoaded && processMode && offered.length === 0) {
    catalogHint = `${locationName ? `An ${locationName}` : 'An der Prozess-Location'} wird laut Katalog kein Zusatz angeboten – Preise und Abweichungen je Location pflegst du auf der Preisliste.`;
  }

  return (
    <div className="addOnRows">
      {list.length === 0
        ? <p className="addOnRowsEmpty">{emptyText ?? 'Keine Zusätze.'}</p>
        : <ul className="addOnRowList">{list.map(renderRow)}</ul>}

      {catalogHint && (
        <p className="addOnRowsHint">
          <span className="material-symbols-rounded" aria-hidden="true">info</span>
          {catalogHint}
        </p>
      )}

      <div className="addOnRowsAdd">
        <ActionButton icon="add" label="Zusatz hinzufügen" type={3} onClick={addRow} disabled={disabled} />
      </div>
    </div>
  );
}

export default AddOnRowsEditor;
